/**
 * SnC-only screen tooltips (not shared with Golf/Spa/Retail common pages).
 * Shared foundation (Outlet Setup, Category Groups, Retail Categories, …)
 * continues to use golfTooltipHeaders via common components.
 */

export type SnCStandardTooltipGroup = {
  header: string;
  code: string;
  name: string;
  active: string;
};

function standard(screen: string): SnCStandardTooltipGroup {
  return {
    header: `Help text for the ${screen} page.`,
    code: `Code for this ${screen} record.`,
    name: `Name / description for this ${screen} record.`,
    active: `Whether this ${screen} record is active.`,
  };
}

/** groupKey used by Admin + runtime (must match tooltipCatalog). */
export function sncTooltipGroupKey(screen: string): string {
  const camel = screen
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, c: string) => c.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, "");
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  return `snc${pascal}Tooltip`;
}

export const sncManagersTooltip = {
  header: "Help text for the Managers page. Edit this copy in Tooltip Admin.",
  mappingCode: "Unique mapping code for this manager record.",
  managerType:
    "Manager role type: CATERINGMANAGER, CONFERENCESERVICEMANAGER, or SALESMANAGER.",
  isActive: "Whether this manager is active in Visual One.",
  managerFirstName: "Manager first name.",
  managerLastName: "Manager last name.",
};

export const sncSalesCallsCallTypesTooltip = standard("Sales Calls Call Types");
export const sncSalesCallsNextStepsTooltip = standard("Sales Calls Next Steps");
export const sncCancelCodesTooltip = standard("Cancel Codes");
export const sncContactTypeTooltip = standard("Contact Type");
export const sncArrivalTypesTooltip = standard("Arrival Types");
export const sncReservedByCodesTooltip = standard("Reserved By Codes");
export const sncCancellationPolicyTooltip = {
  ...standard("Cancellation Policy"),
  name: "Cancellation policy text / description.",
};
export const sncPotentialCodesTooltip = standard("Potential Codes");
export const sncDenialCodesTooltip = standard("Denial Codes");
export const sncDistributionListTooltip = standard("Distribution List");
export const sncHolidaysAndHotDatesTooltip = standard("Holidays and Hot Dates");
export const sncFunctionRoomSetupTooltip = standard("Function Room Setup");
export const sncFunctionRoomTypeFeeTooltip = standard("Function Room Type Fee");
export const sncFunctionTypesTooltip = standard("Function Types");
export const sncFunctionRoomsFeaturesTooltip = standard(
  "Function Rooms Features",
);
export const sncFunctionRoomTooltip = standard("Function Room");
export const sncFoodTypesTooltip = standard("Food Types");
export const sncUnitsOfMeasureTooltip = standard("Units of Measure");
export const sncMenuItemsTooltip = standard("Menu Items");
export const sncSetupItemsTooltip = standard("Setup Items");
export const sncBeverageItemsTooltip = standard("Beverage Items");
export const sncMiscellaneousItemsTooltip = standard("Miscellaneous Items");
export const sncAudioVisualItemsTooltip = standard("Audio Visual Items");
export const sncRoomAndTaxTooltip = standard("Room and Tax");
export const sncIncidentalsTooltip = standard("Incidentals");

/** Off-nav / legacy manager list pages still in the codebase. */
export const sncCompetitorProfileTooltip = standard("Competitor Profile");
export const sncCateringManagersTooltip = standard("Catering Managers");
export const sncCateringManagerTooltip = standard("Catering Manager");
export const sncSalesManagerTooltip = standard("Sales Manager");
export const sncConferenceServiceManagerTooltip = standard(
  "Conference Service Manager",
);

/** Screen label → tooltip defaults object (SnC-only + off-nav). */
export const SNC_TOOLTIP_BY_SCREEN: Record<
  string,
  SnCStandardTooltipGroup | typeof sncManagersTooltip
> = {
  Managers: sncManagersTooltip,
  "Sales Calls Call Types": sncSalesCallsCallTypesTooltip,
  "Sales Calls Next Steps": sncSalesCallsNextStepsTooltip,
  "Cancel Codes": sncCancelCodesTooltip,
  "Contact Type": sncContactTypeTooltip,
  "Arrival Types": sncArrivalTypesTooltip,
  "Reserved By Codes": sncReservedByCodesTooltip,
  "Cancellation Policy": sncCancellationPolicyTooltip,
  "Potential Codes": sncPotentialCodesTooltip,
  "Denial Codes": sncDenialCodesTooltip,
  "Distribution List": sncDistributionListTooltip,
  "Holidays and Hot Dates": sncHolidaysAndHotDatesTooltip,
  "Function Room Setup": sncFunctionRoomSetupTooltip,
  "Function Room Type Fee": sncFunctionRoomTypeFeeTooltip,
  "Function Types": sncFunctionTypesTooltip,
  "Function Rooms Features": sncFunctionRoomsFeaturesTooltip,
  "Function Room": sncFunctionRoomTooltip,
  "Food Types": sncFoodTypesTooltip,
  "Units of Measure": sncUnitsOfMeasureTooltip,
  "Menu Items": sncMenuItemsTooltip,
  "Setup Items": sncSetupItemsTooltip,
  "Beverage Items": sncBeverageItemsTooltip,
  "Miscellaneous Items": sncMiscellaneousItemsTooltip,
  "Audio Visual Items": sncAudioVisualItemsTooltip,
  "Room and Tax": sncRoomAndTaxTooltip,
  Incidentals: sncIncidentalsTooltip,
  "Competitor Profile": sncCompetitorProfileTooltip,
  "Catering Managers": sncCateringManagersTooltip,
  "Catering Manager": sncCateringManagerTooltip,
  "Sales Manager": sncSalesManagerTooltip,
  "Conference Service Manager": sncConferenceServiceManagerTooltip,
};

/** Shared multi-product pages — do not seed snc*Tooltip for these. */
export const SNC_SHARED_FOUNDATION_SCREENS = [
  "Property Information",
  "Outlet Setup",
  "Category Groups",
  "Retail Categories",
  "Retail Items Setup",
] as const;
